import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTitulos } from '../actions/titulosActions';
import { createCertificado } from '../actions/socioActions';
import QRCode from 'qrcode';

// Guardia global por sesión (evita múltiples creates por socio/tipo si hay remounts)
const emittedSessionKeys = new Set();

const CertificadoRegistro = ({ show, handleClose, socio }) => {
  const dispatch = useDispatch();
  const { titulos } = useSelector(state => state.titulo);

  const [gradoTexto, setGradoTexto] = useState('ingeniero');
  const [isSaving, setIsSaving] = useState(false);
  const [certUrl, setCertUrl] = useState('');
  const [certCodigo, setCertCodigo] = useState('');
  const [pdfDataUri, setPdfDataUri] = useState('');

  // 1) Cargar títulos
  useEffect(() => {
    const sid = socio?.id || socio?._id;
    if (sid) dispatch(fetchTitulos(sid));
  }, [dispatch, socio]);

  // 2) Calcular gradoTexto
  useEffect(() => {
    if (titulos && titulos.length > 0) {
      const grados = titulos.map(t => t.grado);
      if (grados.every(g => g === 10)) setGradoTexto('técnico');
      else if (grados.includes(1)) setGradoTexto('ingeniero');
      else setGradoTexto('ingeniero');
    }
  }, [titulos]);

  // 3) Limpiar al cerrar
  useEffect(() => {
    if (!show) {
      setIsSaving(false);
      setCertUrl('');
      setCertCodigo('');
      setPdfDataUri('');
      const container = document.getElementById('pdf-preview');
      if (container) container.innerHTML = '';
    }
  }, [show]);

  const loadImage = async (url) => {
    const res = await fetch(url);
    return res.arrayBuffer();
  };

  const extractCodeFromUrl = (url) => {
    try {
      if (!url) return '';
      const u = new URL(url, window.location.origin);
      const path = u.pathname.replace(/\/+$/,''); // sin trailing slash
      const segs = path.split('/');
      const last = segs[segs.length - 1];
      return decodeURIComponent(last || '');
    } catch {
      // fallback manual
      const clean = (url || '').split('?')[0].split('#')[0];
      const parts = clean.split('/');
      return decodeURIComponent(parts.pop() || '');
    }
  };

  // Payload JSON para el QR cuando no hay URL pública
  const buildQRPayload = useCallback(() => {
    const hoyISO = new Date().toISOString().slice(0, 10);
    const titulosLimpios = Array.isArray(titulos)
      ? titulos.map(t => ({
          grado: t.grado,
          titulo: t.titulo || t.nombre || '',
          universidad: t.suniv || t.universidad || socio?.suniv || '',
          nrotit: t.nrotit || socio?.nrotit || '',
          fecha: t.fecha || t.fechatitulo || ''
        }))
      : [];

    return JSON.stringify({
      entidad: 'CIAB - Filial Tarija',
      tipo: 'Certificado de Afiliación',
      generado: hoyISO,
      socio: {
        id: socio?.id || socio?._id,
        codreg: socio?.codreg,
        ci: socio?.ci,
        nombres: socio?.nombres,
        apPat: socio?.apPat,
        apMat: socio?.apMat,
        genero: socio?.genero,
        gradoTexto,
        nrotit: socio?.nrotit,
        suniv: socio?.suniv,
        telefono: socio?.telefono || socio?.celular || '',
        email: socio?.email || socio?.correo || '',
      },
      titulos: titulosLimpios
    });
  }, [socio, gradoTexto, titulos]);

  // 4) GRABAR en backend y devolver URL/CÓDIGO
  const emitirYObtenerUrl = useCallback(async () => {
    const socioId = socio?.id || socio?._id;
    if (!socioId) {
      toast.error('Faltan datos del socio (id).');
      return { url: null, codigo: null };
    }
    try {
      setIsSaving(true);

      const data = await dispatch(
        createCertificado({
          socio,
          tipoUi: 'REGISTRO',
          titulos,
          gradoTexto
        })
      );

      const codigo = data?.certificado?.codigo || '';
      if (codigo) setCertCodigo(codigo);

      if (codigo) {
        const urlFront = `${window.location.origin}/ver-certificado/${encodeURIComponent(codigo)}`;
        setCertUrl(urlFront);
        toast.success('Certificado creado correctamente.');
        return { url: urlFront, codigo };
      }

      const hint = data?.public_url_hint || '';
      const abs = hint
        ? (hint.startsWith('http') ? hint : `${window.location.origin}${hint}`)
        : '';

      if (abs) {
        setCertUrl(abs);
        // si no vino código explícito, intenta extraerlo de la URL
        if (!codigo) setCertCodigo(extractCodeFromUrl(abs));
        toast.success('Certificado creado correctamente.');
        return { url: abs, codigo: codigo || extractCodeFromUrl(abs) || '' };
      }

      toast.success('Certificado creado (sin URL pública).');
      return { url: null, codigo: codigo || '' };
    } catch (e) {
      console.error('[emitirYObtenerUrl] Error:', e);
      toast.error('No se pudo registrar el certificado en el servidor.');
      return { url: null, codigo: null };
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, socio, titulos, gradoTexto]);

  // 5) Generar PDF y mostrarlo (recibe código explícito)
  const generatePDF = useCallback(async (qrContentParam, codigoParam) => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]);

      const marginLeft = 113;
      const marginRight = 80;
      const maxWidth = page.getWidth() - marginLeft - marginRight;

      const logoBytes = await loadImage(process.env.PUBLIC_URL + '/assets/cabeceraciab.png');
      const pieBytes  = await loadImage(process.env.PUBLIC_URL + '/assets/pieciab.png');

      const logoImage = await pdfDoc.embedPng(logoBytes);
      const pieImage  = await pdfDoc.embedPng(pieBytes);

      const logoDims = logoImage.scale(0.35);
      const pieDims  = pieImage.scale(0.35);

      // Cabecera
      page.drawImage(logoImage, {
        x: marginLeft - 10,
        y: page.getHeight() - logoDims.height - 40,
        width: logoDims.width,
        height: logoDims.height,
      });

      // Pie
      page.drawImage(pieImage, {
        x: marginLeft - 10,
        y: 20,
        width: pieDims.width,
        height: pieDims.height,
      });

      const font     = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const today = new Date();
      const day   = today.getDate();
      const month = today.toLocaleString('es-ES', { month: 'long' });
      const year  = today.getFullYear();
      const ella  = socio.genero === 'F' ? 'la' : 'el';
      const habi  = socio.genero === 'F' ? 'habilitada' : 'habilitado';
      const insc  = socio.genero === 'F' ? 'inscrita' : 'inscrito';
      const acti  = socio.genero === 'F' ? 'activa' : 'activo';

      // Título
      const title = 'CERTIFICADO DE AFILIACION';
      const titleWidth = fontBold.widthOfTextAtSize(title, 16);
      const titleY = page.getHeight() - logoDims.height - 80;
      page.drawText(title, {
        x: (page.getWidth() - titleWidth) / 2,
        y: titleY,
        size: 16,
        font: fontBold,
        color: rgb(0, 0, 0),
      });

      // Código (usa el param, luego estado, luego lo extrae de la URL)
      const codeToRender = codigoParam || certCodigo || extractCodeFromUrl(certUrl) || '—';
      const codeText = `Código: ${codeToRender}`;
      const codeWidth = font.widthOfTextAtSize(codeText, 12);
      const codeY = titleY - 18;
     /* page.drawText(codeText, {
        x: (page.getWidth() - codeWidth) / 2,
        y: codeY,
        size: 12,
        font,
        color: rgb(0, 0, 0),
      });*/
      const texts = [
        { type: 'text', value: 'El Colegio de Ingenieros Agrónomos y Profesionales de Ciencias Agropecuarias de Bolivia - CIAB Filial Tarija.' },
        { type: 'fragmented', parts: [{ text: 'Certifica:', bold: true }]},
        { type: 'fragmented', parts: [
          { text: `Que, ${ella} ${gradoTexto}: `, bold: false },
          { text: `${socio.apPat} ${socio.apMat} ${socio.nombres}`, bold: true },
          { text: `, con C.I. ${socio.ci}, Titulo en Provisión Nacional N° ${socio.nrotit}, ${socio.suniv}, se encuentra legalmente ${insc} y ${acti} en el CIAB - FILIAL TARIJA, con el registro ${socio.codreg}, encontrándose ${habi} para ejercer la profesión de acuerdo a la Ley N° 37014 del ejercicio profesional del Ingeniero Agrónomo.`, bold: false }
        ]},
        { type: 'text', value: 'El presente certificado tiene una validez de 60 días.' },
        { type: 'text', value: 'Es cuanto certificamos en honor a la verdad y para los fines consiguientes del interesado.' },
        { type: 'textRight', value: `Tarija, ${day} de ${month.charAt(0).toUpperCase() + month.slice(1)} de ${year}` },
      ];

      let y = page.getHeight() - logoDims.height - 120;

      const addJustifiedText = (text, yPos, fnt, size = 12, lineHeight = 14) => {
        const tokens = text.split(/(\s+)/);
        const words = tokens.filter(token => token !== '').map(token => ({ word: token, bold: false }));
        let line = [], lines = [], currentWidth = 0;

        words.forEach(({ word }) => {
          const wordWidth = fnt.widthOfTextAtSize(word, size);
          if (currentWidth + wordWidth > maxWidth && word.trim() !== '') {
            lines.push(line);
            line = [{ word, bold: false }];
            currentWidth = wordWidth;
          } else {
            line.push({ word, bold: false });
            currentWidth += wordWidth;
          }
        });
        if (line.length > 0) lines.push(line);

        lines.forEach((lineWords, index) => {
          let x = marginLeft;
          const isLastLine = index === lines.length - 1;

          const totalTextWidth = lineWords.reduce((sum, { word }) => {
            return sum + fnt.widthOfTextAtSize(word, size);
          }, 0);

          const spaces = lineWords.filter(w => w.word.trim() === '').length;
          const extraSpace = (!isLastLine && spaces > 0) ? (maxWidth - totalTextWidth) / spaces : 0;

          lineWords.forEach(({ word }) => {
            page.drawText(word, { x, y: yPos, size, font: fnt, color: rgb(0, 0, 0) });
            const wordWidth = fnt.widthOfTextAtSize(word, size);
            x += wordWidth;
            if (word.trim() === '' && !isLastLine) x += extraSpace;
          });

          yPos -= lineHeight;
        });

        return yPos;
      };

      const addJustifiedFragments = (parts, yPos, fnt, fntBold, size = 12, lineHeight = 14) => {
        const wordsWithStyle = [];
        parts.forEach(part => {
          const rawWords = part.text.split(/(\s+)/);
          rawWords.forEach(token => {
            if (token.trim() !== '' || token === ' ') {
              wordsWithStyle.push({ word: token, bold: part.bold });
            }
          });
        });

        let line = [], lines = [], currentWidth = 0;
        wordsWithStyle.forEach(({ word, bold }) => {
          const currentFont = bold ? fntBold : fnt;
          const wordWidth = currentFont.widthOfTextAtSize(word, size);
          if (currentWidth + wordWidth > maxWidth && word.trim() !== '') {
            lines.push(line);
            line = [{ word, bold }];
            currentWidth = wordWidth;
          } else {
            line.push({ word, bold });
            currentWidth += wordWidth;
          }
        });
        if (line.length > 0) lines.push(line);

        lines.forEach((lineWords, index) => {
          let x = marginLeft;
          const isLastLine = index === lines.length - 1;

          const totalTextWidth = lineWords.reduce((sum, { word, bold }) => {
            const f = bold ? fntBold : fnt;
            return sum + f.widthOfTextAtSize(word, size);
          }, 0);

          const spaces = lineWords.filter(w => w.word.trim() === '').length;
          const extraSpace = (!isLastLine && spaces > 0) ? (maxWidth - totalTextWidth) / spaces : 0;

          lineWords.forEach(({ word, bold }) => {
            const currentFont = bold ? fntBold : fnt;
            page.drawText(word, { x, y: yPos, size, font: currentFont, color: rgb(0, 0, 0) });
            const wordWidth = currentFont.widthOfTextAtSize(word, size);
            x += wordWidth;
            if (word.trim() === '' && !isLastLine) x += extraSpace;
          });

          yPos -= lineHeight;
        });

        return yPos;
      };

      const addTextRightAligned = (text, yPos, fnt) => {
        const lines = text.split(' ').reduce((acc, word) => {
          let line = acc[acc.length - 1] + ' ' + word;
          if (fnt.widthOfTextAtSize(line, 12) > maxWidth) {
            acc[acc.length - 1] = acc[acc.length - 1].trim();
            acc.push(word);
          } else {
            acc[acc.length - 1] = line;
          }
          return acc;
        }, ['']);
        lines.forEach(line => {
          page.drawText(line, {
            x: page.getWidth() - marginRight - fnt.widthOfTextAtSize(line, 12),
            y: yPos,
            size: 12,
            font: fnt,
            color: rgb(0, 0, 0),
          });
          yPos -= 14;
        });
      };

      // Cuerpo (formato intacto)
      texts.forEach(textObj => {
        if (textObj.type === 'text') {
          y = addJustifiedText(textObj.value, y, font);
          y -= 6;
        } else if (textObj.type === 'fragmented') {
          y = addJustifiedFragments(textObj.parts, y, font, fontBold);
          y -= 6;
        } else if (textObj.type === 'textRight') {
          addTextRightAligned(textObj.value, y, font);
        }
      });

      // === QR: abajo-izquierda ===
      const contentForQR = qrContentParam || certUrl || buildQRPayload();
      const qrDataUrl = await QRCode.toDataURL(contentForQR, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 180,
      });

      const qrRes = await fetch(qrDataUrl);
      const qrBytes = await qrRes.arrayBuffer();
      const qrImage = await pdfDoc.embedPng(qrBytes);

      const qrScale = 140 / qrImage.width;
      const qrDims = qrImage.scale(qrScale);

      const padding = 16;
      const qrX = marginLeft - 10;
      const qrY = 20 + pieDims.height + padding;

    /*  page.drawText('Escanear para verificar', {
        x: qrX,
        y: qrY + qrDims.height + 8,
        size: 10,
        font,
        color: rgb(0, 0, 0),
      });
*/
      page.drawImage(qrImage, {
        x: qrX,
        y: qrY,
        width: qrDims.width,
        height: qrDims.height,
      });

      const dataUri = await pdfDoc.saveAsBase64({ dataUri: true });
      setPdfDataUri(dataUri);

      const container = document.getElementById('pdf-preview');
      if (container) container.innerHTML = `<iframe width='100%' height='600px' src='${dataUri}'></iframe>`;
    } catch (error) {
      console.error(error);
      toast.error('Error al generar el PDF. Por favor verifique los datos del socio...');
    }
  }, [buildQRPayload, socio?.genero, certUrl, certCodigo, gradoTexto, socio]);

  // Al abrir modal: crear una sola vez por sesión y generar PDF
  useEffect(() => {
    if (!show || !socio) return;
    const socioId = socio?.id || socio?._id;
    if (!socioId) return;

    const key = `REGISTRO:${socioId}`;
    if (emittedSessionKeys.has(key)) {
      // Si ya existe en esta sesión, re-genera PDF con lo que haya
      const code = certCodigo || extractCodeFromUrl(certUrl) || '';
      (async () => {
        await generatePDF(certUrl || undefined, code || undefined);
      })();
      return;
    }

    emittedSessionKeys.add(key);

    (async () => {
      const { url, codigo } = await emitirYObtenerUrl();
      await generatePDF(url || undefined, codigo || undefined);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, socio]); // deps mínimas para no rebote

  return (
    <Modal show={show} onHide={handleClose} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton>
        <Modal.Title>Certificado de Afiliación</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div id="pdf-preview"></div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CertificadoRegistro;
